import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { GetMultiplePostsService, GetSavedListsService, GetSavedProspectsService } from "../Services/GetProspectsPosts";
import toast from "react-hot-toast";
import { Context } from "../Store/Store";

/** DigitalOcean App Platform — realtime scraper gateway (Socket.io). Override via REACT_APP_SCRAPER_GATEWAY_URL. */
const DEFAULT_SCRAPER_GATEWAY_URL = "https://kynzo-realtime-scraper-gateway-hucpq.ondigitalocean.app";
const SCRAPER_GATEWAY_URL = process.env.REACT_APP_SCRAPER_GATEWAY_URL || DEFAULT_SCRAPER_GATEWAY_URL;

const NurtureContext = createContext(null);

/** Gateway rejects a second batch while the same user already has a running job (e.g. after refresh + subscribe_job). */
function isAlreadyRunningBatchRejection(payload) {
  return payload?.code === "USER_SCRAPE_JOB_ALREADY_RUNNING";
}

export const NurtureProvider = ({ children }) => {
  const store = useContext(Context);
  const state = store?.[0] ?? null;
  const userId = state?.user?.data?.id;

  const socketRef = useRef(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [socketConnectError, setSocketConnectError] = useState(null);

  /** Latest userId + setters for socket handlers (socket is created once). */
  const socketStateRef = useRef({
    userId: undefined,
    setMessageCount: () => {},
    setBatchProgressTotal: () => {},
    setRefreshPostsKey: () => {},
  });

  // POSTS STATE
  const [orderBy, setOrderBy] = useState("DESC");
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [allPosts, setAllPosts] = useState([]);
  const [refreshPostsKey, setRefreshPostsKey] = useState(0);

  // PROSPECTS STATE
  const [prospects, setProspects] = useState([]);
  const [isLoadingProspects, setIsLoadingProspects] = useState(true);
  const [refreshProspectsKey, setRefreshProspectsKey] = useState(0);

  // LISTS STATE
  const [lists, setLists] = useState([]);
  const [isLoadingLists, setIsLoadingLists] = useState(true);
  const [refreshListsKey, setRefreshListsKey] = useState(0);

  // STATE FOR REALTIME SCRAPE (Socket.io)
  const [postsFound, setPostsFound] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateFinished, setUpdateFinished] = useState(false);
  const [openNoPostsFound, setOpenNoPostsFound] = useState(false);
  const [errorUpdaing, setErrorUpdating] = useState(false);
  const [showCookieErr, setShowCookieErr] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [pourcentage, setPourcentage] = useState(0);
  /** When set, progress bar uses server batch completed/total (monitored run). */
  const [batchProgressTotal, setBatchProgressTotal] = useState(null);

  const setMessageCountRef = useRef(setMessageCount);
  const setBatchProgressTotalRef = useRef(setBatchProgressTotal);
  const setRefreshPostsKeyRef = useRef(setRefreshPostsKey);
  setMessageCountRef.current = setMessageCount;
  setBatchProgressTotalRef.current = setBatchProgressTotal;
  setRefreshPostsKeyRef.current = setRefreshPostsKey;

  useEffect(() => {
    socketStateRef.current = {
      userId,
      setMessageCount: (...args) => setMessageCountRef.current(...args),
      setBatchProgressTotal: (...args) => setBatchProgressTotalRef.current(...args),
      setRefreshPostsKey: (...args) => setRefreshPostsKeyRef.current(...args),
    };
  }, [userId]);

  // SOCKET.IO CONNECTION
  useEffect(() => {
    const socket = io(SCRAPER_GATEWAY_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
    });
    socketRef.current = socket;

    const emitSubscribeIfReady = () => {
      const uid = socketStateRef.current.userId;
      if (uid != null && uid !== "") {
        socket.emit("subscribe_job", { userId: uid });
      }
    };

    const onConnect = () => {
      setIsSocketConnected(true);
      setSocketConnectError(null);
      emitSubscribeIfReady();
    };
    const onDisconnect = () => {
      setIsSocketConnected(false);
    };
    const onConnectError = (err) => {
      setSocketConnectError(err?.message || "Connection failed");
    };

    const onJobResume = (data) => {
      if (data?.error || data?.active === false) return;
      const { userId: uid, setMessageCount: smc, setBatchProgressTotal: sbt, setRefreshPostsKey: srk } =
        socketStateRef.current;
      if (uid != null && data?.userId != null && String(data.userId) !== String(uid)) return;
      if (
        data?.active &&
        typeof data.completed === "number" &&
        typeof data.total === "number" &&
        data.total > 0
      ) {
        smc(data.completed);
        sbt(data.total);
        if (data.completed > 0) {
          srk((k) => k + 1);
        }
      }
    };

    const onJobProgress = (data) => {
      if (typeof data?.completed !== "number" || typeof data?.total !== "number") return;
      const { setMessageCount: smc, setBatchProgressTotal: sbt } = socketStateRef.current;
      sbt(data.total);
      smc(Math.min(data.completed, data.total));
    };

    const onJobRegistered = (data) => {
      if (typeof data?.total !== "number") return;
      const { setMessageCount: smc, setBatchProgressTotal: sbt } = socketStateRef.current;
      sbt(data.total);
      smc(0);
    };

    const onJobFinished = (ev) => {
      if (isAlreadyRunningBatchRejection(ev)) {
        return;
      }
      const { setBatchProgressTotal: sbt, setRefreshPostsKey: srk } = socketStateRef.current;
      sbt(null);
      srk((k) => k + 1);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("job_resume", onJobResume);
    socket.on("job_progress", onJobProgress);
    socket.on("job_registered", onJobRegistered);
    socket.on("job_finished", onJobFinished);

    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("job_resume", onJobResume);
      socket.off("job_progress", onJobProgress);
      socket.off("job_registered", onJobRegistered);
      socket.off("job_finished", onJobFinished);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  /** When userId loads after mount, subscribe to any active server-side job. */
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket?.connected || userId == null || userId === "") return;
    socket.emit("subscribe_job", { userId });
  }, [userId]);

  /**
   * Legacy single-prospect emit (gateway maps to one-prospect batch when Redis is enabled).
   * Does not bump messageCount; prefer waitForMonitoringBatch for monitored runs.
   */
  const startScrape = useCallback(({ userId: uid, prospectId, linkedinProfileUrl }) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit("start_scrape", {
      userId: uid,
      prospectId,
      linkedinProfileUrl,
    });
  }, []);

  /**
   * One user job: many prospects. Server uses stable job rooms (reconnect-safe with Redis).
   * Resolves when `job_finished` is received for this batch.
   */
  const waitForMonitoringBatch = useCallback(
    ({ userId: batchUserId, prospects, onScrapePost, onScrapeError }) => {
      return new Promise((resolve) => {
        const socket = socketRef.current;
        if (!socket) {
          resolve();
          return;
        }
        const ids = new Set(prospects.map((p) => String(p.prospectId)));

        const handleScrapePost = (data) => {
          if (!ids.has(String(data?.prospectId))) return;
          onScrapePost?.(data);
        };

        const handleScrapeError = (data) => {
          if (isAlreadyRunningBatchRejection(data)) {
            return;
          }
          const pid = data?.prospectId;
          if (pid != null && !ids.has(String(pid))) return;
          if (data?.jobId) {
            const prefixes = [...ids].map((id) => `job-${id}-`);
            if (!prefixes.some((prefix) => String(data.jobId).startsWith(prefix))) return;
          }
          onScrapeError?.(data);
        };

        const handleJobFinished = (ev) => {
          if (isAlreadyRunningBatchRejection(ev)) {
            return;
          }
          cleanup();
          setBatchProgressTotalRef.current(null);
          resolve();
        };

        const cleanup = () => {
          socket.off("scrape_post", handleScrapePost);
          socket.off("scrape_error", handleScrapeError);
          socket.off("job_finished", handleJobFinished);
        };

        socket.on("scrape_post", handleScrapePost);
        socket.on("scrape_error", handleScrapeError);
        socket.on("job_finished", handleJobFinished);

        socket.emit("start_monitoring_batch", { userId: batchUserId, prospects });
      });
    },
    []
  );

  /**
   * Subscribe to gateway events for a single prospect. Always call the returned cleanup when done
   * (e.g. after work_finished or scrape_error for that job).
   * Transport reconnect no longer ends the waiter as an error (server job rooms are stable).
   */
  const subscribeScrapeEvents = useCallback((prospectId, { onScrapePost, onWorkFinished, onScrapeError }) => {
    const socket = socketRef.current;
    if (!socket) {
      return () => {};
    }

    const jobPrefix = `job-${prospectId}-`;

    const handleScrapePost = (data) => {
      if (data?.prospectId !== prospectId) return;
      onScrapePost?.(data);
    };

    const handleWorkFinished = (data) => {
      if (data?.prospectId !== prospectId) return;
      onWorkFinished?.(data);
    };

    const handleScrapeError = (data) => {
      if (data?.jobId && !String(data.jobId).startsWith(jobPrefix)) return;
      onScrapeError?.(data);
    };

    socket.on("scrape_post", handleScrapePost);
    socket.on("work_finished", handleWorkFinished);
    socket.on("scrape_error", handleScrapeError);

    return () => {
      socket.off("scrape_post", handleScrapePost);
      socket.off("work_finished", handleWorkFinished);
      socket.off("scrape_error", handleScrapeError);
    };
  }, []);

  //  Reset state
  const getInitialState = () => {
    setIsUpdating(false);
    setUpdateFinished(false);
    setPostsFound([]);
    setErrorUpdating(false);
    setOpenNoPostsFound(false);
    setPourcentage(0);
    setMessageCount(0);
    setBatchProgressTotal(null);
    setShowCookieErr(false);
  };

  // Get saved lists
  useEffect(() => {
    GetSavedListsService()
      .then((res) => {
        res.json().then((json) => {
          setLists(json.data);
        });
      })
      .finally(() => {
        setIsLoadingLists(false);
      });
  }, [refreshListsKey]);

  // Get saved posts
  useEffect(() => {
    const sort = {
      field: "post date",
      order: orderBy,
    };
    GetMultiplePostsService({ sort: sort })
      .then((res) => {
        res.json().then((json) => {
          setAllPosts(json.data);
        });
      })
      .finally(() => {
        setIsLoadingPosts(false);
      });
  }, [orderBy, refreshPostsKey]);

  //   Get saved prospects
  useEffect(() => {
    setIsLoadingProspects(true);
    GetSavedProspectsService()
      .then((res) => {
        res.json().then((json) => {
          setProspects(json.data);
        });
      })
      .finally(() => {
        setIsLoadingProspects(false);
      });
  }, [refreshProspectsKey]);

  //   Calculate pourcentage (server-driven when batchProgressTotal is set)
  useEffect(() => {
    if (batchProgressTotal != null && batchProgressTotal > 0) {
      setPourcentage(Math.round((100 * messageCount) / batchProgressTotal));
      return;
    }
    if (!prospects || prospects.length === 0) return;
    const elementPourcentage = 100 / prospects.length;
    const totalPourcentage = Math.round(elementPourcentage * messageCount);

    setPourcentage(totalPourcentage);
  }, [prospects, messageCount, batchProgressTotal]);

  // send toast notification when scraping is finished and user changes page
  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname !== "/NurtureProspects/posts" && updateFinished) {
      toast.success(
        <span>
          Post updates completed. &nbsp;
          <a style={{ fontWeight: "700", textDecoration: "underline" }} href="/NurtureProspects/posts">
            Show
          </a>
        </span>,
        {
          duration: 10000,
        }
      );
    }
  }, [updateFinished]);

  return (
    <NurtureContext.Provider
      value={{
        startScrape,
        waitForMonitoringBatch,
        subscribeScrapeEvents,
        isSocketConnected,
        socketConnectError,
        isUpdating,
        setIsUpdating,
        updateFinished,
        setUpdateFinished,
        postsFound,
        setPostsFound,
        errorUpdaing,
        setErrorUpdating,
        openNoPostsFound,
        setOpenNoPostsFound,
        pourcentage,
        getInitialState,
        orderBy,
        setOrderBy,
        allPosts,
        setRefreshPostsKey,
        isLoadingPosts,
        showCookieErr,
        setShowCookieErr,
        // PROSPECTS
        prospects,
        isLoadingProspects,
        setIsLoadingProspects,
        setRefreshProspectsKey,

        // LISTS
        lists,
        isLoadingLists,
        setIsLoadingLists,
        setRefreshListsKey,

        setMessageCount,
        setBatchProgressTotal,
      }}
    >
      {children}
    </NurtureContext.Provider>
  );
};

export const useNurtureContext = () => useContext(NurtureContext);
